// Authentication Utilities
import { supabase } from '../lib/supabase';
import { 
  Client, Admin, ClientFormData, AdminFormData, ClientLoginData, AuthResponse,
  ClientAccount, ClientAccountLoginData, ClientAccountFormData 
} from '../types/auth';
import { logger } from './logger';

// Hash password using a simple method (in production, use bcrypt server-side)
export const hashPassword = async (password: string): Promise<string> => {
  // For now, we'll use a simple hash. In production, this should be done server-side with bcrypt
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Authenticate Admin
export const authenticateAdmin = async (credentials: AdminFormData): Promise<AuthResponse> => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: credentials.email,
    password: credentials.password
  });
  if (error || !data.user) throw error || new Error('Invalid credentials');

  // Verify admin role by checking admin_users mapping
  const { data: adminRow, error: adminErr } = await supabase
    .from('admin_users')
    .select('id, name, email')
    .eq('auth_user_id', data.user.id)
    .single();
  if (adminErr || !adminRow) throw new Error('Not an admin');

  const admin: Admin = {
    id: adminRow.id,
    name: adminRow.name,
    email: adminRow.email,
    role: 'admin'
  };
  return { user: admin, userType: 'admin' };
};

// Authenticate Client
export const authenticateClient = async (credentials: ClientLoginData): Promise<AuthResponse> => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: credentials.email,
    password: credentials.password
  });
  if (error || !data.user) throw error || new Error('Invalid credentials');

  // Resolve client by slug and ensure this user is mapped
  const { data: clientRow, error: clientErr } = await supabase
    .from('clients')
    .select('id, name, slug, email, logo_url, is_active, auth_user_id')
    .eq('slug', credentials.slug)
    .eq('auth_user_id', data.user.id)
    .single();
  if (clientErr || !clientRow) throw new Error('Unauthorized for this client');

  const client: Client = {
    id: clientRow.id,
    name: clientRow.name,
    slug: clientRow.slug,
    email: clientRow.email,
    logo_url: clientRow.logo_url,
    is_active: clientRow.is_active
  };
  return { user: client, userType: 'client' };
};

// Authenticate Client Account (Viewer/Editor)
export const authenticateClientAccount = async (credentials: ClientAccountLoginData): Promise<AuthResponse> => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: credentials.email,
    password: credentials.password
  });
  
  if (error) {
    // Provide specific error messages for common issues
    if (error.message.includes('Email not confirmed')) {
      throw new Error('Account not activated. Please contact your administrator to activate this account.');
    }
    throw error;
  }
  
  if (!data.user) throw new Error('Invalid credentials');

  // First get the client account
  const { data: accountRow, error: accountErr } = await supabase
    .from('client_accounts')
    .select('id, client_id, auth_user_id, account_type, name, email, is_active')
    .eq('auth_user_id', data.user.id)
    .eq('is_active', true)
    .single();
  
  if (accountErr || !accountRow) throw new Error('Account not found or inactive');
  
  // Then get the client info to verify slug
  const { data: clientRow, error: clientErr } = await supabase
    .from('clients')
    .select('id, name, slug, is_active')
    .eq('id', accountRow.client_id)
    .single();
  
  if (clientErr || !clientRow) throw new Error('Client not found');
  
  // Verify the client slug matches
  if (clientRow.slug !== credentials.slug) throw new Error('Unauthorized for this client');
  if (!clientRow.is_active) throw new Error('Client is inactive');

  const clientAccount: ClientAccount = {
    id: accountRow.id,
    client_id: accountRow.client_id,
    auth_user_id: accountRow.auth_user_id,
    account_type: accountRow.account_type,
    name: accountRow.name,
    email: accountRow.email,
    is_active: accountRow.is_active
  };

  return { 
    user: clientAccount, 
    userType: accountRow.account_type as 'viewer' | 'editor'
  };
};

// Create Client (Admin only)
export const createClient = async (clientData: ClientFormData): Promise<Client> => {
  try {
    // Generate slug from name if not provided
    const slug = clientData.slug || clientData.name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .trim();

    const { data, error } = await supabase
      .from('clients')
      .insert({
        name: clientData.name,
        slug: slug,
        email: clientData.email,
        logo_url: clientData.logo_url,
        auth_user_id: clientData.auth_user_id
      })
      .select()
      .single();

    if (error) throw error;

    // Create default dashboard settings
    await supabase
      .from('client_dashboard_settings')
      .insert({
        client_id: data.id,
        theme_color: '#00d4ff',
        show_revenue: true,
        show_ga: true,
        show_facebook: true,
        show_linkedin: true
      });

    return data;
      } catch (error) {
      throw error;
    }
};

// Get All Clients (Admin only)
export const getAllClients = async (): Promise<Client[]> => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
      } catch (error) {
      throw error;
    }
};

// Update Client (Admin only)
export const updateClient = async (clientId: string, updates: Partial<ClientFormData>): Promise<Client> => {
  try {
    let updateData: any = { ...updates };
    
    // Hash password if it's being updated
    if (updates.password) {
      updateData.password_hash = await hashPassword(updates.password);
      const { password, ...dataWithoutPassword } = updateData;
      updateData = dataWithoutPassword;
    }

    // Note: Image deletion logic removed - images will be kept in storage

    const { data, error } = await supabase
      .from('clients')
      .update(updateData)
      .eq('id', clientId)
      .select()
      .single();

    if (error) throw error;
    return data;
      } catch (error) {
      throw error;
    }
};

// Delete Client (Admin only)
export const deleteClient = async (clientId: string): Promise<void> => {
  try {
    // Delete the client from database
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', clientId);

    if (error) throw error;
    // Note: Client logo files are kept in storage
      } catch (error) {
      throw error;
    }
};

// Get Client by Slug
export const getClientBySlug = async (slug: string): Promise<Client | null> => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('slug', slug)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // No rows returned
      }
      throw error;
    }

    return data;
      } catch (error) {
      throw error;
    }
};

// Local Storage Helpers
export const saveAuthState = (authData: AuthResponse) => {
  localStorage.setItem('authState', JSON.stringify(authData));
};

export const getAuthState = (): AuthResponse | null => {
  try {
    const stored = localStorage.getItem('authState');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const clearAuthState = () => {
  localStorage.removeItem('authState');
};

// Validate slug format
export const validateSlug = (slug: string): boolean => {
  const slugRegex = /^[a-z0-9-]+$/;
  return slugRegex.test(slug) && slug.length >= 3 && slug.length <= 50;
};

// Generate random password
export const generatePassword = (): string => {
  const length = 12;
  const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

// Client Account Management Functions

// Create Client Account (Admin only)
export const createClientAccount = async (accountData: ClientAccountFormData): Promise<ClientAccount> => {
  try {
    // Store current session to restore later
    const currentSession = await supabase.auth.getSession();
    
    // First create the Supabase auth user using signup
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: accountData.email,
      password: accountData.password,
      options: {
        emailRedirectTo: undefined, // No redirect URL
        data: {
          admin_created: true // Mark as admin-created for tracking
        }
      }
    });

    if (authError || !authData.user) throw authError || new Error('Failed to create auth user');

    // Auto-confirm the email for admin-created accounts
    if (authData.user.id) {
      try {
        // Use SQL to directly confirm the email
        await supabase.rpc('confirm_admin_user', {
          user_id: authData.user.id
        });
      } catch (confirmError) {
        logger.warn('Could not auto-confirm user, they may need manual confirmation:', confirmError);
      }
    }

    // Restore the admin session
    if (currentSession.data.session) {
      await supabase.auth.setSession(currentSession.data.session);
    }

    // Then create the client account record
    const { data, error } = await supabase
      .from('client_accounts')
      .insert({
        client_id: accountData.client_id,
        auth_user_id: authData.user.id,
        account_type: accountData.account_type,
        name: accountData.name,
        email: accountData.email,
        is_active: true
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Get Client Accounts (Admin only)
export const getClientAccounts = async (clientId?: string): Promise<ClientAccount[]> => {
  try {
    let query = supabase
      .from('client_accounts')
      .select(`
        id, client_id, auth_user_id, account_type, name, email, is_active, created_at,
        clients(name, slug)
      `)
      .order('created_at', { ascending: false });

    if (clientId) {
      query = query.eq('client_id', clientId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    throw error;
  }
};

// Update Client Account (Admin only)
export const updateClientAccount = async (accountId: string, updates: Partial<ClientAccountFormData>): Promise<ClientAccount> => {
  try {
    // Note: Password updates are not supported without admin API access
    // Users would need to use "Forgot Password" flow to reset their password
    
    // Update the client account record (exclude password)
    const { password, ...updateData } = updates;
    const { data, error } = await supabase
      .from('client_accounts')
      .update(updateData)
      .eq('id', accountId)
      .select()
      .single();

    if (error) throw error;
    
    if (password) {
      logger.warn('Password update skipped - requires admin API access. User should use password reset flow.');
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// Delete Client Account (Admin only)
export const deleteClientAccount = async (accountId: string): Promise<void> => {
  try {
    // For now, just delete from client_accounts table
    // The auth user will remain but won't have access without a client_account record
    const { error: deleteError } = await supabase
      .from('client_accounts')
      .delete()
      .eq('id', accountId);

    if (deleteError) throw deleteError;

    // Note: We're not deleting the auth user because we don't have admin API access
    // The user won't be able to login without a client_account record
  } catch (error) {
    throw error;
  }
};
